import React, { useEffect, JSX } from 'react';
import { Typography, LinearProgress, AppHeader, Container, Stack } from '@ace-clublabs/shared-component-library';
import Box from '@mui/material/Box';

const COMPLETED_PROGRESS = 100;
const INTERVAL_TIME = 100;
const DELAY_BEFORE_CALLBACK = 500;

export interface LoadingProps {
  message: string;
  finishProps: {
    finished: boolean;
    onFinish: () => void;
  };
  subMessage?: string;
  estimatedTimeInSeconds?: number;
}

export const LoadingView: (props: LoadingProps) => JSX.Element = ({
  estimatedTimeInSeconds,
  message,
  subMessage,
  finishProps: { finished, onFinish },
}) => {
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        const timeInSeconds = estimatedTimeInSeconds ?? 11;
        const intervalProgress = (INTERVAL_TIME / (timeInSeconds * 1000)) * 100;
        return Math.min(oldProgress + intervalProgress, COMPLETED_PROGRESS);
      });
    }, INTERVAL_TIME);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (finished) {
      setProgress(() => COMPLETED_PROGRESS);
      setTimeout(() => {
        onFinish();
      }, DELAY_BEFORE_CALLBACK);
    }
  }, [finished]);

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          paddingLeft: '1rem',
          paddingRight: '1rem',
        }}
      >
        <AppHeader />
        <Stack alignItems="center" sx={{ marginTop: '3.5rem' }}>
          <div className="loading-view">
            <div className="progress-bar-container">
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  className="progress-bar"
                  variant="determinate"
                  value={progress}
                  sx={{ height: '8px', backgroundColor: '#DFF0FE' }}
                />
              </Box>
            </div>
            <div className="loading-info">
              <div className="loading-description-container">
                <Typography id="loading-message" variant="b1Medium">
                  {message}
                </Typography>
                <Typography variant="b1Regular" id="loading-sub-message">
                  {subMessage || "This will only take a moment. Please don't refresh this page or go back."}
                </Typography>
              </div>
            </div>
          </div>
        </Stack>
      </Box>
    </Container>
  );
};
